import { bookingGetTotalPrice, bookingIsHappening, getOfferPax } from '#lib/stores/booking.js';
import { offerStore } from '#lib/stores/offer.js';
import {
	PaymentMethod,
	PaymentStatus,
	type Booking,
	type Payment
} from '#lib/types/generated/index.js';
import { get } from 'svelte/store';

export type PaymentSummary = {
	onlinePaid: number;
	onsitePaid: number;
	giftcardPaid: number;
	canceledAmount: number;
	totalPaid: number;
	remaining: number;
	total: number;
};

export function getPaymentBookingAmount(payment: Payment): number {
	const amount = payment.bookingAmount || payment.amount || 0;
	return typeof amount === 'number' ? amount : 0;
}

export function bookingPaymentGetAmountRefunded(
	payment: Payment,
	allPayments: Payment[] = []
): number {
	return allPayments
		.filter(
			(p) =>
				(p.parentPaymentId === payment.id || p.parentPayment?.id === payment.id) &&
				p.status === PaymentStatus.Success
		)
		.reduce((acc, p) => acc + Math.abs(p.amount || p.bookingAmount || 0), 0);
}

export function bookingGetPaymentSummary(booking: Booking): PaymentSummary {
	const payments = booking.payments.filter((payment) => payment.status === PaymentStatus.Success);
	const total = Number(bookingGetTotalPrice(booking)) || 0;
	const onlinePaid = payments
		.filter((payment) => payment.method === PaymentMethod.Online)
		.reduce((acc, payment) => acc + getPaymentBookingAmount(payment), 0);
	const onsitePaid = payments
		.filter((payment) => payment.method === PaymentMethod.Pos)
		.reduce((acc, payment) => acc + getPaymentBookingAmount(payment), 0);
	const giftcardPaid = payments
		.filter((payment) => payment.method === PaymentMethod.Giftcard)
		.reduce((acc, payment) => acc + getPaymentBookingAmount(payment), 0);
	const canceledAmount = !bookingIsHappening(booking) ? total : 0;
	const totalPaid = onlinePaid + onsitePaid + giftcardPaid;
	const rawRemaining = total - canceledAmount - totalPaid;
	const remaining = isNaN(rawRemaining) ? 0 : Math.round(rawRemaining * 100) / 100;

	return {
		onlinePaid: isNaN(onlinePaid) ? 0 : Math.round(onlinePaid * 100) / 100,
		onsitePaid: isNaN(onsitePaid) ? 0 : Math.round(onsitePaid * 100) / 100,
		giftcardPaid: isNaN(giftcardPaid) ? 0 : Math.round(giftcardPaid * 100) / 100,
		canceledAmount: isNaN(canceledAmount) ? 0 : Math.round(canceledAmount * 100) / 100,
		totalPaid: isNaN(totalPaid) ? 0 : Math.round(totalPaid * 100) / 100,
		remaining,
		total: isNaN(total) ? 0 : Math.round(total * 100) / 100
	};
}

export function bookingGetDefaultGuaranteeAmount(booking: Booking): number {
	const offers = get(offerStore);
	return booking.offers.reduce((acc, offerOrder) => {
		const offerPrice =
			(offers.find((offer) => offer.id === offerOrder.offerId)?.basePrice ?? 0) +
			// Guest prices
			offerOrder.prices.reduce((sum, price) => sum + price.quantity * price.unitPrice, 0);

		const o = offers.find((o) => o.id === offerOrder.offerId);
		if (!o) return acc;

		if (
			o.allowGuaranteedPayment &&
			(!o.guaranteeRequiredFrom || o.guaranteeRequiredFrom <= booking.pax)
		) {
			acc += o.guaranteePrice || 0;
			acc += ((o.guaranteePercent || 0) * offerPrice) / 100;
			acc += (o.guaranteePricePerPax || 0) * booking.pax;
		}

		return acc;
	}, 0);
}

export function bookingIsMissingPrices(booking: Booking): boolean {
	const offers = booking.offers.map((offer) =>
		get(offerStore).find((o) => o.id === offer.offerId)
	);
	return (
		getOfferPax(booking) !== booking.pax &&
		!offers.every((offer) => offer?.guestPrices.filter((gp) => !gp.archivedAt).length === 0)
	);
}
